/**
 * Supported v2 question types.
 *
 * The form renderer (`getQuestionComponent`) switches on `question.questionType` to pick the
 * matching input. This is the single source of truth for the v2 schema and replaces the legacy
 * v1 component-id mapping that was removed in the v1 → v2 migration.
 */
export const QUESTION_TYPES = {
  SHORT_TEXT: "SHORT_TEXT", // single-line text
  LONG_TEXT: "LONG_TEXT", // multi-line text
  MULTI_CHOICE: "MULTI_CHOICE", // select exactly one option (radio)
  CHECKBOX: "CHECKBOX", // select one or more options (checkbox group)
  DROPDOWN: "DROPDOWN", // select exactly one option (dropdown)
  MULTISELECT: "MULTISELECT", // select one or more options (multi dropdown)
  MULTISELECT_TAGS: "MULTISELECT_TAGS", // rating-aware tag pills — handled only by CourseFeedbackForm
  NUMBER: "NUMBER", // numeric input
  RATING: "RATING", // star rating
  FILE_UPLOAD: "FILE_UPLOAD", // file upload — job forms
  VIDEO: "VIDEO", // record/upload a video — job forms; answer stores the video URL
  DATE_PICKER: "DATE_PICKER", // pick a date
  TIME_PICKER: "TIME_PICKER", // pick a time (placeholder — not built yet)
  DATE_TIME_PICKER: "DATE_TIME_PICKER", // pick a date and time
  GEO_PICTURE: "GEO_PICTURE" // photo captured on device with the coordinates and time it was taken
} as const;

/**
 * Answers to the attendance gate on a GEO_CAPTURE form. The camera is offered only for PRESENT.
 *
 * tenant-admin owns the canonical list (`containers/FormBuilder/helper.tsx` ATTENDANCE_ANSWERS) and
 * locks the question's options so these strings stay stable; this is the matching copy in a separate
 * repo. Change one, change both.
 */
export const ATTENDANCE_ANSWERS = {
  PRESENT: "Present",
  ABSENT: "Absent"
} as const;

/**
 * The internship field an OFF_CAMPUS_REQUEST question fills, as its `metadata.internshipField`.
 * The off-campus request layout finds its seeded questions by this marker, never by label or position,
 * because admins may reword and reorder them.
 *
 * tenant-admin seeds the markers (`containers/FormBuilder/helper.tsx` INTERNSHIP_FIELDS) and core-api
 * copies the answers into `placementMeta.internship` on approval
 * (`helpers/_v3/offCampusRequests/internshipFields.ts`); this is the matching copy. Change one, change all.
 */
export const INTERNSHIP_FIELDS = {
  COMPANY: "company",
  ROLE: "role",
  COMPANY_WEBSITE: "companyWebsite",
  LOCATION: "location",
  STIPEND: "stipend",
  START_DATE: "startDate",
  END_DATE: "endDate",
  MODE: "mode",
  IRF_DOCUMENT: "irfDocument",
  OFFER_LETTER: "offerLetter"
} as const;

export type InternshipField = (typeof INTERNSHIP_FIELDS)[keyof typeof INTERNSHIP_FIELDS];
